import { FieldValues } from 'react-hook-form';

import { SHIPMENT_IMPORT_TABLE_COLUMNS } from '@/components/modules/shipment/import/constants';
import { CargoesApiShipment } from '@/lib/api/providers/shipment/cargoes/types';
import { SHIPMENT_GROUPING, SHIPMENT_TRACKING_TYPES } from '@/lib/constants';
import { TrackingRequestModel } from '@/lib/models/tracking-request/types';
import { CargoTypes, CargoUnitValue, MapMarker, ShipmentStatus, ShipmentSubStatus, VehicleType } from '@/types';
import { PortApiInterface, SegmentModeType } from '@/types/api-types';

import { ShipmentJourneyModel } from '../shipment-journey/types';

export const SHIPMENT_TYPES = {
  AIR: 'air',
  INTERMODAL: 'intermodal',
  PARCEL: 'parcel',
} as const;

export type ShipmentType = (typeof SHIPMENT_TYPES)[keyof typeof SHIPMENT_TYPES];

export interface CreateTrackingRequestBody {
  type: ShipmentType;
  containerNumber?: string;
  mblNumber?: string;
  awbNumber?: string;
  bookingNumber?: string;
  tags?: string[];
  purchaseOrders?: string[];
  skus?: string[];
  oceanLine?: string; // Air carriers not supported in Cargoes API
  parcelNumber?: string;
}

export interface ShipmentModel extends CreateTrackingRequestBody {
  id?: string;
  externalId?: string;
  createdAt?: string;
  updatedAt?: string;
  trackingUpdatedAt?: string | null;
  status?: ShipmentStatus;
  subStatus?: ShipmentSubStatus;
  carrier?: string | null;
  carrierId?: string | null;
  originPortCode?: string | null;
  originPortName?: string | null;
  destinationPortCode?: string | null;
  destinationPortName?: string | null;
  eta?: string | null;
  ata?: string | null;
  etd?: string | null;
  atd?: string | null;
  delay?: number | null;
  tags?: string[];
  lastCheckedAt?: string;
  originalEta?: string;
  milestones?: ShipmentMilestone[];
  journey?: ShipmentJourneyModel;
  cargo?: ShipmentCargo;
  trackingData?: ShipmentTrackingDataModel;
  containerCount?: number;
  associatedContainers?: Omit<ShipmentModel, 'containerCount' | 'associatedContainers'>[];
}

export interface ShipmentsCount {
  status: ShipmentStatus;
  count: number;
}

export interface ShipmentMilestone {
  id: string;
  label: string;
  milestoneCode: string;
  date: string;
  locationName: string;
  locationCode: string;
  completed: boolean;
  hasCargo: boolean;
}

export interface ShipmentCargo {
  type: CargoTypes;
  quantity: number;
  container?: ShipmentContainer;
  volume?: CargoUnitValue;
  weight?: CargoUnitValue;
  packageType?: string;
  packageQuantity?: string;
}

export interface ShipmentContainer {
  size?: string;
  type?: string;
  iso?: string;
}

export interface OrgsShipmentModel {
  id?: string;
  orgId: string;
  shipmentId: string;
  createdAt?: string;
  updatedAt?: string;
  createdById?: string;
  purchaseOrders?: string[];
  skus?: string[];
  apiKeyId?: string;
}

export interface ShipmentVehicleModel {
  imoNumber?: string;
  tripNumber?: string;
  name?: string;
  type?: SegmentModeType;
  carrier?: string;
  fromDate?: string;
  toDate?: string;
  isActive?: boolean;
}

export interface ShipmentJourneyTimelineItem {
  status: string;
  progressPercent: string;
  mode: SegmentModeType | VehicleType;
  locationName?: string;
  locationCode?: string;
  port?: PortApiInterface;
}

export interface ShipmentTrackingDataModel extends Omit<CargoesApiShipment, 'shipmentEvents' | 'emissions'> {
  carrierName: string;
  emissions?: string;
  status: ShipmentStatus;
  subStatus2: ShipmentSubStatus;
  milestones?: ShipmentMilestone[];
  shipmentJourney?: ShipmentJourneyModel;
  markers?: MapMarker[]; // @deprecated
  vehicles?: ShipmentVehicleModel[]; // @deprecated
  journey?: ShipmentJourneyTimelineItem[]; // @deprecated
}

export interface ShipmentFilterPort {
  name?: string;
  code?: string;
}

export interface ShipmentFilters {
  originPortCode: ShipmentFilterPort[];
  destinationPortCode: ShipmentFilterPort[];
  carrier: string[];
  type: string[];
  status: string[];
  subStatus: string[];
}

export interface ShipmentsImportRowData {
  [SHIPMENT_TRACKING_TYPES.CONTAINER_NUMBER]?: string;
  [SHIPMENT_TRACKING_TYPES.AWB_NUMBER]?: string;
  [SHIPMENT_IMPORT_TABLE_COLUMNS.TAGS]: string;
  [SHIPMENT_IMPORT_TABLE_COLUMNS.CARRIER]?: string;
}

export interface ShipmentsImportFormValues {
  rows: FieldValues[];
}

export interface ShipmentChangeModel {
  id?: string;
  shipmentId: string;
  original: Partial<ShipmentModel>;
  updated: Partial<ShipmentModel>;
  createdAt?: string;
}

export interface ShipmentMeta {
  purchaseOrders?: string[];
  skus?: string[];
}

export type ShipmentGrouping = (typeof SHIPMENT_GROUPING)[keyof typeof SHIPMENT_GROUPING];

export type TrackingNumberType = (typeof SHIPMENT_TRACKING_TYPES)[keyof typeof SHIPMENT_TRACKING_TYPES];

export type ShipmentDebugModel = ShipmentModel & {
  orgs_shipments: { id: string; createdAt: string; org: { name: string } }[];
};

export type TrackingRequestDebugModel = TrackingRequestModel & { org: { name: string }; user: { name: string } };
